import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { RadioButtonChecked, Warning } from '@mui/icons-material'
import cn from '../../../../../lib/utils/cn'
import { Input, Tooltip } from '../../../../ui'
import { FormValues } from '../index'
import {
	Plan,
	PaymentPeriodType,
} from '../../../../../lib/api/types/billing.interfaces'
import { CurrentUserObject } from '../../../../../lib/api/types/user.interfaces'
import {
	isCurrentPurchasedPlan,
	getUsersValidationError,
	getMinimumUsers,
	getPlanMinimumUsers,
	parsePlanName,
} from '../utils'

interface PlanUsersControlProps {
	planName: string
	isActive: boolean
	currentPlan?: Plan
	currentUser?: CurrentUserObject
	period: PaymentPeriodType
	selectedPriceId?: string
	canSelect: boolean
	onPlanChange: (planName: string) => void
}

const PlanUsersControl: React.FC<PlanUsersControlProps> = ({
	planName,
	isActive,
	currentPlan,
	currentUser,
	period,
	selectedPriceId,
	canSelect,
	onPlanChange,
}) => {
	const { control, clearErrors } = useFormContext<FormValues>()

	if (planName === 'Free') return null

	if (planName === 'Pro') {
		return (
			<div className="flex items-center justify-start gap-2">
				<RadioButtonChecked
					className={cn(
						'w-4 h-4',
						isActive
							? 'fill-accent'
							: 'fill-text-secondary dark:fill-text-main-dark',
					)}
				/>
				<span
					className={cn(
						'text-text-secondary dark:text-text-main-dark font-medium text-base',
						isActive && 'text-text-main dark:text-text-main-dark',
					)}
				>
					1 User
				</span>
			</div>
		)
	}

	if (planName === 'Business' || planName === 'Enterprise') {
		const fieldName =
			planName === 'Business' ? 'business_users' : 'enterprise_users'
		const totalUsers = currentUser?.plan?.totalUsers
		const planMinUser =
			parsePlanName(currentPlan?.name) === planName
				? currentPlan?.minUsers
				: undefined
		const minimumUsers = getMinimumUsers(
			planName,
			planMinUser,
			totalUsers,
		)
		const isTrial = Boolean(currentPlan?.trial)
		const isCurrentPlan = !isTrial && isCurrentPurchasedPlan(
			planName,
			period,
			currentPlan,
			selectedPriceId,
			currentUser?.plan?.priceId,
		)
		const displayUsers =
			currentPlan?.maxUsers ??
			currentUser?.plan?.maxUsers ??
			getPlanMinimumUsers(planName, planMinUser)

		return (
			<Controller
				control={control}
				name={fieldName}
				rules={{
					validate: (value) =>
						getUsersValidationError(
							planName,
							value,
							totalUsers,
							planMinUser,
						) || true,
				}}
				render={({ field: { value, onChange }, fieldState: { error } }) => {
					if (isCurrentPlan) {
						return (
							<div className="flex items-center justify-start gap-2">
								<RadioButtonChecked
									className={cn(
										'w-4 h-4',
										isActive
											? 'fill-accent'
											: 'fill-text-secondary dark:fill-text-main-dark',
									)}
								/>
								<span
									className={cn(
										'text-text-secondary dark:text-text-main-dark font-medium text-base',
										isActive && 'text-text-main dark:text-text-main-dark',
									)}
								>
									{displayUsers} Users
								</span>
							</div>
						)
					}

					const hasError = isActive && Boolean(error)

					return (
						<div className="flex items-center justify-start gap-2">
							{hasError ? (
								<Tooltip
									label={error?.message}
									classNameChildrenWrapper="h-4 flex items-center justify-center"
								>
									<Warning className="w-4 h-4 fill-negative hover:opacity-80 duration-default cursor-pointer" />
								</Tooltip>
							) : (
								<RadioButtonChecked
									className={cn(
										'w-4 h-4',
										isActive
											? 'fill-accent'
											: 'fill-text-secondary dark:fill-text-main-dark',
									)}
								/>
							)}
							<Input
								wrapperClassName="w-16 h-8"
								inputClassName="px-2 rounded"
								error={hasError ? error?.message : undefined}
								errorClassName="hidden"
								type="number"
								value={value ?? ''}
								onFocus={() => {
									if (!isActive && canSelect) onPlanChange(planName)
								}}
								onChange={(e) => {
									const raw = e?.currentTarget?.value
									const nextValue = raw === '' ? '' : Number(raw)
									onChange(nextValue)
									if (
										nextValue !== '' &&
										!getUsersValidationError(
											planName,
											nextValue,
											totalUsers,
											planMinUser,
										)
									) {
										clearErrors(fieldName)
									}
								}}
								min={minimumUsers}
							/>
							<span
								className={cn(
									hasError && 'text-negative dark:text-negative-dark',
								)}
							>
								Users
							</span>
						</div>
					)
				}}
			/>
		)
	}

	return null
}

export default PlanUsersControl
